import { Field } from '../models/field.model';
import { FieldProperty } from '../models/field-property.model';
import { AppSchemaCategory, FieldsName } from '../../../models/app-token';

export class DefaultFieldService {

  generateFields(category: string): Field[] {
    switch (category) {
      case AppSchemaCategory.SYSTEM_SCHEMAS: {
        return this.systemDefaultFields();
      }
      case AppSchemaCategory.CONTACT_FORMS: {
        return this.contactDefaultFields();
      }
      case AppSchemaCategory.DEFAULT_SCHEMAS: {
        return this.defaultDefaultFields();
      }
      case AppSchemaCategory.CONTENT_SCHEMAS: {
        return this.contentDefaultFields();
      }
      case AppSchemaCategory.SCHEMA_AS_A_PAGE: {
        return this.pageDefaultFields();
      }
      default: {
        return this.generateDefaultFields();
      }
    }
  }

  systemDefaultFields(): Field[] {
    return this.generateDefaultFields();
  }
  contactDefaultFields(): Field[] {
    return this.generateDefaultFields();
  }
  defaultDefaultFields(): Field[] {
    return this.generateDefaultFields();
  }
  contentDefaultFields(): Field[] {
    return this.generateDefaultFields();
  }
  pageDefaultFields(): Field[] {
    return this.generateDefaultFields().concat([
      this.pageNameField(),
      this.pageMetatitle(),
      this.pageUrl(),
      this.pageMetaDescription(),
      this.pageMetaKeyWord(),
      this.pageParentId(),
      this.pageThumbnail(),
      this.pageIsActive(),
      this.pagePriority(),
      this.pageIsSecure(),
      this.pageIsNoIndex()
    ]);
  }


  pageIsActive(): Field {
    const name = new Field();
    name.name = FieldsName.ISACTIVE;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.editor = 'Toggle';
    name.properties.isRequired = false;
    name.properties.fieldType = 'Boolean';
    name.properties.label = 'Is active';

    return name;
  }

  pagePriority(): Field {
    const name = new Field();
    name.name = FieldsName.PRIORITY;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.editor = 'Input';
    name.properties.isRequired = false;
    name.properties.fieldType = 'String';
    name.properties.label = 'Priority';
    name.properties.defaultValue = '1';

    return name;
  }

  pageIsSecure(): Field {
    const name = new Field();
    name.name = FieldsName.ISSECURE;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.editor = 'Toggle';
    name.properties.isRequired = false;
    name.properties.fieldType = 'Boolean';
    name.properties.label = 'Is secure';

    return name;
  }

  pageIsNoIndex(): Field {
    const name = new Field();
    name.name = FieldsName.ISNOINDEX;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.editor = 'Toggle';
    name.properties.isRequired = false;
    name.properties.fieldType = 'Boolean';
    name.properties.label = 'not indexed';

    return name;
  }

  pageNameField(): Field {
    const name = new Field();
    name.name = FieldsName.PAGE_NAME;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.editor = 'Input';
    name.properties.isRequired = false;
    name.properties.fieldType = 'String';
    name.properties.label = 'Page Name';

    return name;
  }

  pageThumbnail(): Field {
    const name = new Field();
    name.name = FieldsName.THUMBNAIL;

    name.partitioning = 'invariant';
    name.properties = new FieldProperty();
    name.properties.fieldType = 'Assets';
    name.properties.isRequired = false;
    name.properties.label = 'Page Thumbnail';
    name.properties.mustBeImage = true;
    name.properties.resolveFirst = false;
    name.properties.resolveImage = false;

    return name;
  }

  pageMetatitle(): Field {
    const metatitle = new Field();
    metatitle.name = FieldsName.METATITLE;
    metatitle.partitioning = 'language';
    metatitle.properties = new FieldProperty();
    metatitle.properties.editor = 'Input';
    metatitle.properties.isRequired = false;
    metatitle.properties.fieldType = 'String';
    metatitle.properties.label = 'MetaTitle';

    return metatitle;
  }

  pageUrl(): Field {
    const url = new Field();
    url.name = FieldsName.URL;

    url.partitioning = 'language';
    url.properties = new FieldProperty();
    url.properties.editor = 'Input';
    url.properties.isRequired = false;
    url.properties.fieldType = 'String';
    url.properties.label = 'Url';
    url.properties.hints = 'end point of the page';
    // url.properties.pattern = "^(\/(([a-zA-Z0-9$\-_.+!*'(),;: @&=]|% [0 - 9a - fA - F]{ 2 })* (\/([a-zA-Z0-9$\-_.+!*'(),;:@&=]|%[0-9a-fA-F]{2})*)*)?(\?([a-zA-Z0-9$\-_.+!*'(),;:@&=?]|%[0-9a-fA-F]{2})*)?(\#([a-zA-Z0-9$\-_.+!*'(),;:@&=?]|%[0-9a-fA-F]{2})*)?)?$";

    return url;
  }

  pageParentId(): Field {
    const parentId = new Field();
    parentId.name = FieldsName.PARENT_ID;

    parentId.partitioning = 'invariant';
    parentId.properties = new FieldProperty();
    parentId.properties.editor = 'Input';
    parentId.properties.label = 'Parent id';
    parentId.properties.isRequired = false;
    parentId.properties.fieldType = 'String';
    return parentId;
  }

  pageMetaDescription(): Field {
    const metaDesc = new Field();
    metaDesc.name = FieldsName.META_DESCRIPTION;

    metaDesc.partitioning = 'language';
    metaDesc.properties = new FieldProperty();
    metaDesc.properties.minLength = 0;
    metaDesc.properties.maxLength = 160;
    metaDesc.properties.editor = 'Input';
    metaDesc.properties.isRequired = false;
    metaDesc.properties.fieldType = 'String';
    metaDesc.properties.label = 'MetaDescription';

    return metaDesc;
  }

  pageMetaKeyWord(): Field {
    const metaKW = new Field();
    metaKW.name = FieldsName.META_KEYWORDS;

    metaKW.partitioning = 'language';
    metaKW.properties = new FieldProperty();
    metaKW.properties.minLength = 0;
    metaKW.properties.maxLength = 160;
    metaKW.properties.editor = 'Input';
    metaKW.properties.isRequired = false;
    metaKW.properties.fieldType = 'String';
    metaKW.properties.label = 'MetaKeywords';

    return metaKW;
  }

  generateDefaultFields(): Field[] {
    return [
      this.generateCodeField(),
      this.generatePublishField(),
      this.generateSoftDeleteField(),
      this.generateDeleteDateTimeField(),
      this.generateIndexField()
    ];
  }

  generateCodeField(): Field {
    const publishField = new Field();
    publishField.name = 'code';

    publishField.partitioning = 'invariant';
    publishField.properties = new FieldProperty();
    publishField.properties.editor = 'Input';
    publishField.properties.isRequired = true;
    publishField.properties.isUnique = false;
    publishField.properties.label = 'Code';
    publishField.properties.fieldType = 'String';
    return publishField;
  }

  generatePublishField(): Field {
    const publishField = new Field();
    publishField.name = 'publishdatetime';

    publishField.partitioning = 'invariant';
    publishField.properties = new FieldProperty();
    publishField.properties.editor = 'DateTime';
    publishField.properties.fieldType = 'DateTime';
    return publishField;
  }

  generateSoftDeleteField(): Field {
    const field = new Field();
    field.name = 'deleted';

    field.partitioning = 'invariant';
    field.properties = new FieldProperty();
    field.properties.editor = 'Toggle';
    field.properties.defaultValue = 'false';
    field.properties.fieldType = 'Boolean';
    return field;
  }

  generateDeleteDateTimeField(): Field {
    const field = new Field();
    field.name = 'deletedatetime';

    field.partitioning = 'invariant';
    field.properties = new FieldProperty();
    field.properties.editor = 'DateTime';

    field.properties.fieldType = 'DateTime';
    return field;
  }

  generateIndexField(): Field {
    const field = new Field();
    field.name = 'index';

    field.partitioning = 'invariant';
    field.properties = new FieldProperty();

    field.properties.fieldType = 'Number';
    return field;
  }

}
